import { ReservationLoadRequestBody, ReservationLoadResponse } from "../sharedcomponents/HallOccupancyGraph";
import { HallReservation, TableReservation } from "../wholehallreservation/WholeHallReservation";
import { Dayjs } from "dayjs";

type makeReservationPayloadSchema = {
    persons: number,
    namesOfGuests: string[],
    objectId: number,
    hallId: number,
    startDate: number,
    endDate: number,
    tableId?: number,
    isMemberAttending: boolean,
    isChildAttending: boolean,
    comment: string,
};

type updateReservationPayloadSchema = {
    reservationId: number,
    namesOfGuests: string[],
    isMemberAttending: boolean,
    isChildAttending: boolean,
    tableId?: number,
    comment: string,
};

type TableReservationPostType = {
    tableId: number;
    guests: string[];
}

type HallReservationPostType = {
    guests: string[];
}

type makeWholeHallReservationPayloadSchema = {
    objectId: number,
    hallId: number,
    startDate: number,
    endDate: number,
    tableReservations: TableReservationPostType[],
    hallReservations: HallReservationPostType[],
    comment: string,
};

export function makeTimestampFromDateAndDate(date: Date, time: Date) {
    let d: Date = new Date(date);
    d.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
    return d.getTime();
}

export async function apiMakeReservation(
    date: Dayjs,
    startTime: Dayjs,
    endTime: Dayjs,
    objectId: number,
    hallId: number,
    tableId: number | undefined,
    namesOfTheGuests: string[],
    isTheMemberGuest: boolean,
    isTheChildGuest: boolean,
    comment: string,
) {
    const url = `${process.env.REACT_APP_API_PREFIX}/reservations/api/makereservation`;

    const startDate = Math.floor(makeTimestampFromDateAndDate(date.toDate(), startTime.toDate()) / 1000);
    const endDate = Math.floor(makeTimestampFromDateAndDate(date.toDate(), endTime.toDate()) / 1000);

    const payload: makeReservationPayloadSchema = {
        namesOfGuests: namesOfTheGuests,
        persons: namesOfTheGuests.length,
        objectId: objectId,
        hallId: hallId,
        startDate: startDate,
        endDate: endDate,
        isMemberAttending: isTheMemberGuest,
        isChildAttending: isTheChildGuest,
        comment: comment,
        tableId: tableId,
    };

    return await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
    }).then(async (response) => {
        try {
            const jsonResponse = await response.json();
            return { status: response.status, jsonResponse: jsonResponse };
        } catch (reason) {
            console.log(`Error decoding json with the following reason: ${reason}`);
            return { status: response.status };
        }
    }).catch((reason) => {
        console.log(`Make reservation request failed with the following reason: ${reason}`);
        throw reason;
    });
}

export async function apiUpdateReservation(
    reservationId: number,
    namesOfTheGuests: string[],
    isMemberAttending: boolean,
    isChildAttending: boolean,
    tableId: number | undefined,
    comment: string,
) {
    const url = `${process.env.REACT_APP_API_PREFIX}/reservations/api/updatereservation`

    const payload: updateReservationPayloadSchema = {
        reservationId: reservationId,
        namesOfGuests: namesOfTheGuests,
        isMemberAttending: isMemberAttending,
        isChildAttending: isChildAttending,
        tableId: tableId,
        comment: comment,
    };

    return await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
    }).then(async (response) => {
        const jsonResponse = await response.json();
        return { status: response.status, jsonResponse: jsonResponse };
    }).catch((reason) => {
        console.log(`Update reservation request failed with the following reason: ${reason}`);
        throw reason;
    });
}

export async function apiOnSearchMember(searchString: string, mode: number, signal?: AbortSignal,) {
    return await fetch(`${process.env.REACT_APP_API_PREFIX}/reservations/api/autocompletedata?memberQueryString=${encodeURIComponent(searchString)}&mode=${encodeURIComponent(mode)}`,
        {
            credentials: 'include',
            signal: signal
        }
    );
}

export async function apiFetchTableFreeTime(objectId: number, hallId: number, tableId: number, startDate: Date, endDate: Date) {
    const startSeconds = Math.floor(startDate.getTime() / 1000) + 1;
    const endSeconds = Math.floor(endDate.getTime() / 1000);

    return await fetch(`${process.env.REACT_APP_API_PREFIX}/reservations/api/gettablefreetime?objectId=${encodeURIComponent(objectId)}&hallId=${encodeURIComponent(hallId)}&tableId=${encodeURIComponent(tableId)}&startTimestamp=${encodeURIComponent(startSeconds)}&endTimestamp=${encodeURIComponent(endSeconds)}`, {
        credentials: 'include'
    }).then((response) => response.json())
        .then((value) => { return value })
        .catch((reason) => {
            console.log(`FetchTableFreeTime failed with the following reason ${reason}`);
            throw reason;
        });
}

export async function apiMakeWholeHallReservation(
    objectId: number,
    hallId: number,
    date: Dayjs,
    startTime: Dayjs,
    endTime: Dayjs,
    tableReservations: TableReservation[] | undefined,
    hallReservations: HallReservation[] | undefined,
    comment: string,
) {
    let tres: TableReservationPostType[] = [];
    if (tableReservations) {
        for (const tableReservation of tableReservations) {
            tres.push({ tableId: tableReservation.table.id, guests: tableReservation.guests });
        }
    }

    let hres: HallReservationPostType[] = [];
    if (hallReservations) {
        for (const hallReservation of hallReservations) {
            hres.push({ guests: hallReservation.guests });
        }
    }

    let postObject: makeWholeHallReservationPayloadSchema = {
        startDate: makeTimestampFromDateAndDate(date.toDate(), startTime.toDate()) / 1000,
        endDate: makeTimestampFromDateAndDate(date.toDate(), endTime.toDate()) / 1000,
        objectId: objectId,
        hallId: hallId,
        comment: comment,
        tableReservations: tres,
        hallReservations: hres,
    };

    return await fetch(`${process.env.REACT_APP_API_PREFIX}/reservations/api/makewholehallreservation`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(postObject),
        credentials: 'include',
    });
}

export async function apiIsHallEmpty(objectId: number, hallId: number, date: Dayjs, start: Dayjs, end: Dayjs) {
    let requestBody: ReservationLoadRequestBody[] = [];
    requestBody.push({
        start: makeTimestampFromDateAndDate(date.toDate(), start.toDate()) / 1000 + 1,
        end: makeTimestampFromDateAndDate(date.toDate(), end.toDate()) / 1000,
    });

    return await fetch(`${process.env.REACT_APP_API_PREFIX}/reservations/api/gethalloccupancydata`,
        {
            method: 'POST',
            body: JSON.stringify({
                'objectId': objectId,
                'hallId': hallId,
                'intervals': requestBody
            }),
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        }
    ).then(async response => {
        const apiResponse = await response.json();
        return apiResponse[0].reservedPlaces === 0;
    }).catch(reason => {
        console.error(reason);
        return undefined;
    });
}

export async function fetchSingleReservationTotalDue(
    date: Dayjs,
    startTime: Dayjs,
    endTime: Dayjs,
    objectId: number,
    hallId: number,
    tableId: number | undefined,
    namesOfTheGuests: string[],
    isTheMemberGuest: boolean,
    isTheChildGuest: boolean,
    comment: string,
) {
    throw new Error("Currently Unimplemented");
}

export async function apiFetchWholeHallReservationTotalDue(
    objectId: number,
    hallId: number,
    startDate: number,
    endDate: number,
) {
    let postObject = {
        startDate: startDate / 1000,
        endDate: endDate / 1000,
        objectId: objectId,
        hallId: hallId,       
    };

    return await fetch(`${process.env.REACT_APP_API_PREFIX}/reservations/api/getwholehallreservationtotaldue`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(postObject),
        credentials: 'include',
    });
}

export async function apiUpdateWholeHallReservation(
    id: number,
    date: Dayjs,
    startTime: Dayjs,
    endTime: Dayjs,
    tableReservations: TableReservation[] | undefined,
    hallReservations: HallReservation[] | undefined,
    comment: string,
) {
    let tres: TableReservationPostType[] = [];
    if (tableReservations) {
        for (const tableReservation of tableReservations) {
            tres.push({ tableId: tableReservation.table.id, guests: tableReservation.guests });
        }
    }

    let hres: HallReservationPostType[] = [];
    if (hallReservations) {
        for (const hallReservation of hallReservations) {
            hres.push({ guests: hallReservation.guests });
        }
    }

    let postObject = {
        id: id,
        startDate: makeTimestampFromDateAndDate(date.toDate(), startTime.toDate()),
        endDate: makeTimestampFromDateAndDate(date.toDate(), endTime.toDate()),
        comment: comment,
        tableReservations: tres,
        hallReservations: hres,
    };

    return await fetch(`${process.env.REACT_APP_API_PREFIX}/reservations/api/updatewholehallreservation`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(postObject),
        credentials: 'include',
    });
}